<script setup lang="ts">
  import { Icon, IconName, IconSize, Swiper, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, ref, watch } from 'vue';
  import type { Product } from '@/entities/product';
  import RatingBadge from '@/shared/ui/RatingBadge.vue';
  import { HOTELS_MAP_TEXTS } from '../config/texts';
  import { getHotelCardLocation, getHotelCardPhotos, getHotelCardReviews } from '../lib/hotelCardView';
  import { getMapProductPrice } from '../lib/mapHotels';
  import { formatMapPrice } from '../lib/markerViews';
  import type { HotelOpenOptions } from '../model/types';

  interface HotelCardProps {
    product: Product;
  }

  const props = defineProps<HotelCardProps>();

  interface HotelCardEmits {
    (e: 'close'): void;
    (e: 'open', options: HotelOpenOptions): void;
  }

  const emit = defineEmits<HotelCardEmits>();

  const photos = computed<string[]>(() => getHotelCardPhotos(props.product));
  const location = computed<string>(() => getHotelCardLocation(props.product));
  const reviews = computed(() => getHotelCardReviews(props.product));
  const priceLabel = computed<string>(() => formatMapPrice(getMapProductPrice(props.product)));

  const photoIndex = ref(0);

  watch(photos, () => {
    photoIndex.value = 0;
  });
</script>

<template>
  <article
    class="hotel-card"
    data-testid="hotels-map-card"
    @click="emit('open', { toReviews: false })"
  >
    <div class="hotel-card__media">
      <Swiper
        v-if="photos.length"
        v-model="photoIndex"
        class="hotel-card__swiper"
        :images="photos"
      />
      <div
        v-else
        class="hotel-card__media-placeholder"
        role="img"
        :aria-label="HOTELS_MAP_TEXTS.photoAlt"
        data-testid="hotels-map-card-placeholder"
      />

      <button
        class="hotel-card__close"
        type="button"
        :aria-label="HOTELS_MAP_TEXTS.closeCard"
        data-testid="hotels-map-card-close"
        @click.stop="emit('close')"
      >
        <Icon
          :name="IconName.Close"
          :size="IconSize.M"
        />
      </button>
    </div>

    <div class="hotel-card__info">
      <div class="hotel-card__details">
        <Typo
          class="hotel-card__name"
          :variant="TypoVariant.Heading6"
          data-testid="hotels-map-card-name"
        >
          {{ product.hotel?.name }}
        </Typo>

        <div class="hotel-card__meta">
          <div
            class="hotel-card__reviews"
            @click.stop="emit('open', { toReviews: reviews.rating !== null })"
          >
            <RatingBadge
              :rating="reviews.rating"
              testid="hotels-map-card-rating"
            />
            <Typo
              class="hotel-card__reviews-count"
              :variant="TypoVariant.BodyMRegular"
              tag="span"
              data-testid="hotels-map-card-reviews"
            >
              {{ reviews.countLabel }}
            </Typo>
          </div>

          <template v-if="location">
            <Icon
              class="hotel-card__separator"
              :name="IconName.PointFilled"
              :size="IconSize.XXS"
            />
            <Typo
              class="hotel-card__location"
              :variant="TypoVariant.BodyMRegular"
              tag="span"
              data-testid="hotels-map-card-location"
            >
              {{ location }}
            </Typo>
          </template>
        </div>
      </div>

      <div class="hotel-card__price-block">
        <Typo
          class="hotel-card__price"
          :variant="TypoVariant.Heading5"
          tag="span"
          data-testid="hotels-map-card-price"
        >
          {{ priceLabel }}
        </Typo>
      </div>
    </div>
  </article>
</template>

<style scoped lang="scss">
  .hotel-card {
    background: var(--color-bg-primary);
    border-radius: var(--rounding-l);
    box-shadow: var(--shadow-l);
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
    overflow: hidden;
    -webkit-tap-highlight-color: transparent;
    width: 100%;

    &__media {
      height: 150px;
      overflow: hidden;
      position: relative;
      width: 100%;
    }

    &__swiper {
      height: 100%;
      width: 100%;
    }

    &__close {
      align-items: center;
      background: rgb(32 41 57 / 0.5);
      border: none;
      border-radius: 50%;
      color: var(--color-text-inverse);
      cursor: pointer;
      display: flex;
      fill: var(--color-text-inverse);
      height: 28px;
      justify-content: center;
      padding: 0;
      position: absolute;
      right: var(--spacing-s);
      top: var(--spacing-s);
      width: 28px;
      z-index: 1;
    }

    &__media-placeholder {
      background: var(--color-neutral-20);
      height: 100%;
      width: 100%;
    }

    &__info {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      padding: 0 var(--spacing-s) var(--spacing-s);
    }

    &__details {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxs);
    }

    &__name {
      color: var(--color-text-primary);
      overflow-wrap: anywhere;
    }

    &__meta {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-xxxs);
    }

    &__reviews {
      align-items: baseline;
      display: flex;
      gap: var(--spacing-xxs);
    }

    &__reviews-count {
      color: var(--color-text-secondary);
    }

    &__separator {
      color: var(--color-text-secondary);
      fill: var(--color-text-secondary);
      flex-shrink: 0;
    }

    &__location {
      color: var(--color-text-secondary);
    }

    &__price-block {
      align-items: baseline;
      display: flex;
      gap: var(--spacing-xs);
    }

    &__price {
      color: var(--color-text-primary);
    }
  }
</style>
